import { animate, style, transition, trigger } from "@angular/animations";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Component, ElementRef, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { first } from "rxjs";
import { ApiService } from "src/app/shared/services/api.service";
import { ISkippedAndDuplicates, ISkippedErrorCode } from "../../domain/interfaces/metadata.interface";
import { MetadataService } from "../../domain/services/metadata.service";
import { RnaMutationsService } from "../../domain/services/rna-mutations.service";

@Component({
  selector: "app-upload-page",
  templateUrl: "./upload-page.component.html",
  styleUrls: ["./upload-page.component.scss"],
  animations: [
    trigger("fadeInOut", [
      transition(":enter", [style({ opacity: 0 }), animate("600ms ease-in", style({ opacity: 1 }))]),
      transition(":leave", [animate("600ms ease-out", style({ opacity: 0 }))]),
    ]),
  ],
})
export class UploadPageComponent {
  /**
   * Scrolls the feedback container into view whenever it appears.
   * @param el Feedback container element reference
   */
  @ViewChild("feedbackContainer")
  set feedbackContainerSetter(el: ElementRef<HTMLDivElement> | undefined) {
    if (el) {
      el.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }
  @ViewChild("metadataInput") metadataInput!: ElementRef<HTMLInputElement>;

  isUploading = false;
  dots = Array(5); // Animation loader
  messages: string[] = [];

  overviewData: any = null;

  constructor(
    private http: HttpClient,
    private api: ApiService,
    private translateService: TranslateService,
    private metadataService: MetadataService,
    private cdr: ChangeDetectorRef,
    private rnaMutationsService: RnaMutationsService
  ) {}

  /**
   * Whether the user's role allows uploading metadata
   */
  get canUploadMetadata(): boolean {
    return this.metadataService.canUploadSampleMetadata();
  }

  /**
   * Whether the user's role allows uploading RNA-seq and mutations
   */
  get canUploadRnaMutation(): boolean {
    return this.rnaMutationsService.canUpload();
  }

  /**
   * Clears and opens the hidden file input used to select a metadata file.
   */
  triggerMetadataFileInput(): void {
    this.metadataInput.nativeElement.value = "";
    setTimeout(() => this.metadataInput.nativeElement.click(), 0);
  }

  /**
   * Processes the metadata file selected through the file input.
   * @param event Change event from the file input
   */
  handleMetadataFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.processMetadataFile(file);
  }

  /**
   * Prevents the default browser behavior while a file is dragged over the drop zone.
   * @param event Drag event
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Prevents the default browser behavior when a dragged file leaves the drop zone.
   * @param event Drag event
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Processes the metadata file dropped onto the drop zone.
   * @param event Drop event carrying the dropped file
   */
  onDropMetadata(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer?.files?.[0];
    if (file) this.processMetadataFile(file);
  }

  /**
   * Validates and uploads the given metadata file, then reports the resulting messages.
   * @param file Metadata file to upload
   */
  private processMetadataFile(file: File): void {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["csv", "tsv"].includes(extension || "")) {
      const message = this.translateService.instant("uploadData.msg.invalidFormat");
      alert(message);
      return;
    }

    this.messages = [];

    const formData = new FormData();
    formData.append("file", file);

    this.isUploading = true;

    this.metadataService
      .uploadSampleMetadata(formData)
      .pipe(first())
      .subscribe({
        next: (response: ISkippedAndDuplicates) => {
          const { skip, duplicates } = response;

          if (skip?.length) {
            skip.forEach((elem: { sampleName: string; code: ISkippedErrorCode }) => {
              this.messages.push(
                elem.sampleName +
                  " " +
                  this.translateService.instant("uploadData.metadata.msg.skipped") +
                  " : " +
                  this.translateService.instant(`uploadData.metadata.error.${elem.code}`)
              );
            });
          }

          if (duplicates?.length) {
            this.messages.push(this.translateService.instant("uploadData.msg.duplicate") + " " + duplicates.join(", "));
          }

          if (!skip?.length && !duplicates?.length) {
            this.messages.push(this.translateService.instant("uploadData.metadata.msg.ok"));
          }
          this.isUploading = false;
        },
        error: (err: HttpErrorResponse) => {
          const rawMessage = typeof err.error === "string" ? err.error : this.translateService.instant("unknown");

          this.messages.push(this.translateService.instant("uploadData.msg.ko") + " : " + rawMessage);
          this.isUploading = false;
        },
      });
  }
}
